<template>
    <v-container fluid>
        <!-- Freitext -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('Suchen') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.query"
                    dense
                    outlined
                    hide-details="auto"
                    :placeholder="$t('freitext_search')"
                />
            </v-col>
        </v-row>

        <!-- Kategorien -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('kategorien') }}</label>
            </v-col>
            <v-col cols="9">
                <category-select
                    v-model="searchParams.category_ids"
                    :page-id="2"
                    clearable
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                    multiple
                />
            </v-col>
        </v-row>

        <!-- Tags -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('tags') }}</label>
            </v-col>
            <v-col cols="9">
                <tag-select
                    v-model="searchParams.tag_ids"
                    for-address
                    clearable
                    show-download
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                />
            </v-col>
        </v-row>

        <!-- Region -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('region') }}</label>
            </v-col>
            <v-col cols="9">
                <region-select v-model="searchParams.region_id" clearable hide-details="auto" />
            </v-col>
        </v-row>

        <!-- PLZ/Ort -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('ort') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.ort"
                    dense
                    outlined
                    hide-details="auto"
                    :placeholder="$t('plz_ort')"
                />
            </v-col>
        </v-row>

        <!-- Erstellt -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('erstellt') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.created_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.created_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- bearbeitet -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('bearbeitet') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.modified_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.modified_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- Quelle -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('quelle') }}</label>
            </v-col>
            <v-col cols="9">
                <v-select
                    v-model="searchParams.bp_import_origin"
                    :items="importOriginItems"
                    item-value="id"
                    item-text="text"
                    clearable
                    dense
                    outlined
                    hide-details="auto"
                />
            </v-col>
        </v-row>

        <!-- linked in magazin -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('linked_in_magazin') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    v-model="searchParams.linked_in_magazin"
                    row
                    class="mt-0"
                    hide-details="auto"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Kulturpartner -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('kulturpartner') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group v-model="searchParams.is_partner" row class="mt-0" hide-details="auto">
                    <v-radio value="" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Aktive Autoren -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('aktive_autoren') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    v-model="searchParams.is_active"
                    row
                    class="mt-0"
                    hide-details="auto"
                >
                    <v-radio value="" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Login-Status -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('login_status') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group v-model="searchParams.with_login" row class="mt-0" hide-details="auto">
                    <v-radio value="" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('mit_login')" />
                    <v-radio value="false" :label="$t('ohne_login')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Adress-Art -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-start">
                <label class="tgk-paragraph-text">{{ $t('addr_typ') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group v-model="searchParams.type" class="mt-0" hide-details="auto">
                    <v-radio value="address" :label="$t('adresse')" />
                    <v-radio value="" :label="$t('egal')" />
                    <v-radio value="event_organizer" :label="$t('veranstalter')" />
                    <v-radio value="location" :label="$t('ort')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Location: mit Veranstalter -->
        <v-row dense>
            <v-col cols="12" class="d-flex align-start">
                <label class="tgk-paragraph-text">{{ $t('assigned_to_eo') }}</label>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="3" class="d-flex align-start"> </v-col>
            <v-col cols="9">
                <v-radio-group v-model="searchParams.assigned_to_event_organizer" row class="mt-0" hide-details="auto">
                    <v-radio value="" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('mit_veranstalter')" />
                    <v-radio value="false" :label="$t('ohne_veranstalter')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Mailing Lists -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('mailing_listen') }}</label>
            </v-col>
            <v-col cols="9">
                <v-select
                    v-model="searchParams.mailing_list_ids"
                    dense
                    outlined
                    :items="mailingList ? mailingList.models : []"
                    item-text="name"
                    item-value="id"
                    hide-details="auto"
                    multiple
                    :placeholder="$t('choose_multi')"
                    clearable
                >
                    <template #selection="{ item }">
                        <span>{{ item.name }}</span>
                    </template>
                </v-select>
            </v-col>
        </v-row>

        <!-- ID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('id') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.id" type="number" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <!-- UUID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('uuid') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.uuid" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <v-row dense>
            <v-col class="d-flex justify-end">
                <PlainBtn @click="onResetBtn">{{ $t('reset_search') }}</PlainBtn>
                <ActionBtn class="ml-1" @click="onSearchBtn">
                    <KpIcon icon-cls="searchmagnifier" color="white" left />
                    {{ $t('search') }}
                </ActionBtn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import CategorySelect from '@/components/category/select.vue';
import TagSelect from '@/components/tag/select.vue';
import RegionSelect from '@/components/region/select.vue';
import { mapActions, mapState } from 'vuex';

/**
 * Enhanced Search Component for Address search
 *
 * Emits:
 * - search(params): when the user click on "search"
 * - reset: when the user clicks on "reset"
 *
 * The initial search params can be set via initial-params property.
 */
export default {
    name: 'AddressEnhancedSearch',

    components: {
        CategorySelect,
        TagSelect,
        RegionSelect,
    },

    props: {
        // the search params, emitted as 'input' on search btn click or reset
        initialParams: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            searchParams: { ...this.initialParams },
            importOriginItems: [
                { id: 'own', text: this.$t('own') },
                { id: 'guidle', text: 'guidle.com' },
                { id: 'eventfrog', text: 'eventfrog.ch' },
            ],
        };
    },
    computed: {
        ...mapState('mailing_list', {
            mailingList: 'collection',
        }),
    },

    mounted() {
        this.loadMailingLists();
    },

    methods: {
        ...mapActions('mailing_list', {
            loadMailingLists: 'load',
        }),
        getSearchParams() {
            return { ...this.searchParams };
        },

        onSearchBtn() {
            this.emitChangedParams();
        },

        onResetBtn() {
            this.searchParams = {};
            this.$emit('reset', {});
        },

        emitChangedParams() {
            this.$emit('search', { ...this.searchParams });
        },
    },
};
</script>
